/* eslint-disable @typescript-eslint/no-explicit-any */

import type { QueryClient } from '@tanstack/react-query'
import { useCallback, useRef } from 'react'
import {
  hasInstantAddPending,
  isInstantAddSessionTaskId,
} from '@/components/projects/instantAdd/instantAddState'
import { createLogger } from '@/lib/logger'
import { useTaskRealtime } from '@/lib/realtime/features/useTaskRealtime'
import {
  isCompletedTaskForSummary,
  updateParentSubtaskSummaryQueries,
} from '@/lib/tasks/taskSummaryCache'
import { sortTasksByPosition } from '@/utils/fractional-ordering'
import { useQueryClient } from '@tanstack/react-query'

import { Task, TaskWithAssignments } from '@scrambl/shared'

import {
  getLastFieldApplied,
  useTaskRealtimeEditingSync,
} from './useTaskRealtimeEditing'

const logger = createLogger('hooks/useTasksRealtime')

const tasksRealtimeLogState = {
  lastLogTsByKey: new Map<string, number>(),
}

const debugLogTasksRealtime = (
  key: string,
  message: string,
  payload?: unknown,
) => {
  if (!import.meta.env.DEV) return

  const now = Date.now()
  const lastLog = tasksRealtimeLogState.lastLogTsByKey.get(key) ?? 0
  if (now - lastLog < 250) {
    return
  }

  tasksRealtimeLogState.lastLogTsByKey.set(key, now)
  logger.debug(message, payload)
}

type TaskRecord = Record<string, unknown>

const stripUndefinedValues = (value: TaskRecord): Partial<TaskRecord> => {
  const next: Partial<TaskRecord> = {}
  Object.entries(value).forEach(([key, itemValue]) => {
    if (itemValue !== undefined) {
      next[key] = itemValue
    }
  })
  return next
}

const preserveAvailableStatuses = (
  cachedTask: TaskWithAssignments | undefined,
  updates: Partial<TaskRecord>,
): TaskRecord => {
  if (!cachedTask) {
    return updates as TaskRecord
  }

  if (
    (!Object.prototype.hasOwnProperty.call(updates, 'availableStatuses') ||
      updates.availableStatuses == null) &&
    Object.prototype.hasOwnProperty.call(cachedTask, 'availableStatuses')
  ) {
    return {
      ...updates,
      availableStatuses: (cachedTask as TaskRecord).availableStatuses,
    } as TaskRecord
  }

  return updates as TaskRecord
}

const normalizeTaskUpdates = (
  task: Partial<TaskWithAssignments>,
): Partial<TaskWithAssignments> => {
  const cleaned = stripUndefinedValues(task as TaskRecord)
  return cleaned as Partial<TaskWithAssignments>
}

const TASK_MEMBERSHIP_FIELDS = new Set<string>([
  'status',
  'statusTypeId',
  'completedAt',
  'deletedAt',
  'isArchived',
  'groupId',
  'sprintId',
  'cycleInstanceId',
  'assignees',
  'taskAssignments',
  'dueDate',
  'scheduledFor',
  'moduleType',
  'moduleId',
])

const getTaskAssigneeIds = (
  task: Partial<TaskWithAssignments> | undefined,
): string[] => {
  if (!task) return []

  const taskRecord = task as TaskRecord
  const assignees = taskRecord.assignees
  if (Array.isArray(assignees)) {
    return assignees
      .map(assignee => {
        if (typeof assignee === 'string') return assignee
        if (!assignee || typeof assignee !== 'object') return undefined
        const assigneeRecord = assignee as TaskRecord
        if (typeof assigneeRecord.id === 'string') return assigneeRecord.id
        const user = assigneeRecord.user as TaskRecord | undefined
        if (user && typeof user.id === 'string') return user.id
        return undefined
      })
      .filter((id): id is string => !!id)
  }

  const taskAssignments = taskRecord.taskAssignments
  if (Array.isArray(taskAssignments)) {
    return taskAssignments
      .map(assignment => {
        if (!assignment || typeof assignment !== 'object') return undefined
        const assignmentRecord = assignment as TaskRecord
        if (typeof assignmentRecord.userId === 'string') {
          return assignmentRecord.userId
        }
        const user = assignmentRecord.user as TaskRecord | undefined
        if (user && typeof user.id === 'string') return user.id
        return undefined
      })
      .filter((id): id is string => !!id)
  }

  return []
}

const shouldInvalidateTaskMembership = (
  current: TaskWithAssignments | undefined,
  changedKeys: string[],
): boolean => {
  if (!current) {
    return true
  }

  return changedKeys.some(key => TASK_MEMBERSHIP_FIELDS.has(key))
}

const isTaskListQueryKey = (key: readonly unknown[]): boolean => {
  if (key[0] !== 'tasks') return false

  return (
    key[1] !== 'detail' &&
    key[1] !== 'by-number' &&
    key[1] !== 'groups' &&
    key[1] !== 'groups-with-counts'
  )
}

const getTaskQueryModuleId = (key: readonly unknown[]): string | undefined => {
  if (typeof key[2] === 'string') {
    return key[2]
  }

  if (key[2] && typeof key[2] === 'object') {
    const filters = key[2] as Record<string, unknown>
    return typeof filters.moduleId === 'string' ? filters.moduleId : undefined
  }

  return undefined
}

const invalidateMembershipSensitiveTaskQueries = (
  queryClient: QueryClient,
  task: Partial<TaskWithAssignments>,
  current: TaskWithAssignments | undefined,
  changedKeys: string[],
) => {
  if (!shouldInvalidateTaskMembership(current, changedKeys)) {
    return
  }

  const taskRecord = task as TaskRecord
  const currentRecord = current as TaskRecord | undefined
  const nextModuleId =
    typeof taskRecord.moduleId === 'string' ? taskRecord.moduleId : undefined
  const previousModuleId =
    typeof currentRecord?.moduleId === 'string'
      ? currentRecord.moduleId
      : undefined
  const moduleIds = new Set(
    [nextModuleId, previousModuleId].filter((id): id is string => !!id),
  )
  const assigneeIds = new Set([
    ...getTaskAssigneeIds(current),
    ...getTaskAssigneeIds(task),
  ])

  queryClient.invalidateQueries({
    predicate: query => {
      const key = query.queryKey
      if (!Array.isArray(key)) return false

      if (key[0] === 'my-tasks') {
        const queryUserId = typeof key[1] === 'string' ? key[1] : undefined
        if (!queryUserId) return true
        return assigneeIds.has(queryUserId) || moduleIds.has(queryUserId)
      }

      if (!isTaskListQueryKey(key)) return false

      const queryModuleId = getTaskQueryModuleId(key)
      return (
        !queryModuleId || moduleIds.size === 0 || moduleIds.has(queryModuleId)
      )
    },
    refetchType: 'active',
  })
}

/**
 * GLOBAL deduplication map shared across all hook instances.
 * This prevents the same update from being processed multiple times
 * when the hook is mounted in multiple components.
 */
const globalLastUpdateMap = new Map<string, number>()

// Cleanup old global entries periodically
const cleanupGlobalMaps = () => {
  const now = Date.now()
  const maxAge = 60000 // 1 minute

  for (const [key, timestamp] of globalLastUpdateMap.entries()) {
    if (now - timestamp > maxAge) {
      globalLastUpdateMap.delete(key)
    }
  }
}

// Run cleanup every 30 seconds
if (typeof window !== 'undefined') {
  setInterval(cleanupGlobalMaps, 30000)
}

/**
 * Global task realtime subscription hook.
 * Handles list/detail cache sync for task events and delegates edit-coordination
 * to the dedicated task realtime editing hook.
 */
export function useTasksRealtime(enabled = true) {
  const queryClient = useQueryClient()
  // Note: We now use globalLastUpdateMap instead of instance-local lastUpdateRef
  const lastUpdateRef = useRef(globalLastUpdateMap)
  const { applyTaskUpdates: surgicallyUpdateTask } =
    useTaskRealtimeEditingSync()

  // Simplified but effective deduplication
  const shouldSkipUpdate = useCallback(
    (taskId: string, eventData?: any): boolean => {
      const now = Date.now()

      // If this update includes an order change, do NOT time-dedupe. Reorders must always render.
      const isReorder = !!(
        eventData &&
        typeof eventData === 'object' &&
        'order' in eventData
      )

      if (!isReorder) {
        // MOST EFFECTIVE: Simple time-based deduplication per task
        const lastUpdate = lastUpdateRef.current.get(taskId)
        if (lastUpdate && now - lastUpdate < 500) {
          // 500ms window
          return true
        }
      }

      // SECONDARY: Content-based deduplication for identical data (include order and updatedAt)
      if (eventData) {
        const contentKey = `${taskId}-${eventData.status}-${eventData.order ?? ''}-${eventData.updatedAt}`
        const lastContentUpdate = lastUpdateRef.current.get(contentKey)
        if (lastContentUpdate && now - lastContentUpdate < 2000) {
          return true
        }
        lastUpdateRef.current.set(contentKey, now)
      }

      // Update timestamp
      lastUpdateRef.current.set(taskId, now)

      // Cleanup old entries (memory management)
      if (lastUpdateRef.current.size > 1000) {
        const entries = Array.from(lastUpdateRef.current.entries())
        entries.sort((a, b) => b[1] - a[1]) // Newest first
        lastUpdateRef.current.clear()
        entries.slice(0, 500).forEach(([key, timestamp]) => {
          lastUpdateRef.current.set(key, timestamp)
        })
      }

      return false
    },
    [],
  )

  // Set up real-time task updates
  useTaskRealtime({
    enabled,
    onTaskCreated: (task: Task) => {
      debugLogTasksRealtime(
        `onTaskCreated:${task.id}`,
        'useTasksRealtime onTaskCreated',
        {
          taskId: task.id,
          parentTaskId:
            (task as TaskWithAssignments & { parentTaskId?: string })
              .parentTaskId ?? null,
          moduleId:
            (task as TaskWithAssignments & { moduleId?: string }).moduleId ??
            null,
        },
      )

      const tracker = (window as any).__taskCreationTracker as
        | Map<string, number>
        | undefined
      const parentId = (task as any)?.parentTaskId as string | undefined
      const taskModuleId = (task as any)?.moduleId as string | undefined
      const taskName = (task as any)?.name as string | undefined

      // Skip if this is our own creation (check by ID first)
      const idKey = `created-${task.id}`
      if (
        tracker?.get(idKey) &&
        Date.now() - (tracker.get(idKey) || 0) < 5000
      ) {
        debugLogTasksRealtime(
          `onTaskCreated:${task.id}`,
          'useTasksRealtime onTaskCreated skipped: tracker id duplicate',
        )
        return // Skip - already handled by mutation (ID match)
      }

      // Instant-add flow: when createInstantTask has registered an in-flight
      // tempId for this module, skip the realtime processing entirely so its
      // own merge can swap fields onto the optimistic row without remounting
      // it. createInstantTask sets the realtime tracker once its API call
      // resolves, so a normal future realtime event for the same task would
      // hit the idKey path above instead.
      if (taskModuleId && hasInstantAddPending(taskModuleId)) {
        debugLogTasksRealtime(
          `onTaskCreated:${task.id}`,
          'useTasksRealtime onTaskCreated skipped: instant-add in flight for module',
        )
        return
      }

      // Also check by content key for subtasks (handles race condition where realtime arrives before mutation returns)
      if (parentId && taskName && taskModuleId) {
        const contentKey = `subtask-${parentId}-${taskName}-${taskModuleId}`
        if (
          tracker?.get(contentKey) &&
          Date.now() - (tracker.get(contentKey) || 0) < 5000
        ) {
          debugLogTasksRealtime(
            `onTaskCreated:${task.id}`,
            'useTasksRealtime onTaskCreated skipped: tracker content duplicate',
          )
          return // Skip - already handled by mutation (content match)
        }
      }

      // If this is a subtask, ONLY update the subtasks cache - nothing else
      if (parentId) {
        const cacheKey = ['subtasks', parentId] as const
        let shouldUpdateParentSummary = true
        const existing = queryClient.getQueryData<
          TaskWithAssignments[] | undefined
        >(cacheKey)
        if (existing && Array.isArray(existing)) {
          // Check if already exists by real ID
          if (existing.some(t => t.id === task.id)) {
            // DO NOT touch any other queries - subtasks are excluded from main lists
            return
          }

          // Check if there's a temp/optimistic subtask to replace
          // This handles the race condition where realtime arrives after mutation success
          const tempIdx = existing.findIndex(
            t =>
              t.id.startsWith('temp-') &&
              t.name === taskName &&
              t.parentTaskId === parentId,
          )

          if (tempIdx !== -1) {
            // Replace optimistic subtask with real one
            const updated = [...existing]
            updated[tempIdx] = task as TaskWithAssignments
            queryClient.setQueryData(cacheKey, updated)
            shouldUpdateParentSummary = false
          } else {
            // No existing or temp task found - add the new subtask
            queryClient.setQueryData(cacheKey, [
              ...existing,
              task as TaskWithAssignments,
            ])
          }
        }
        if (shouldUpdateParentSummary) {
          updateParentSubtaskSummaryQueries(queryClient, parentId, {
            subtaskDelta: 1,
            completedDelta: isCompletedTaskForSummary(
              task as TaskWithAssignments,
            )
              ? 1
              : 0,
          })
        }
        // DO NOT touch any other queries - subtasks are excluded from main lists
        return
      }

      // For top-level tasks, ONLY update the SPECIFIC module's query - not all queries
      // This prevents cascade re-renders of unrelated project components
      if (!taskModuleId) return

      // Find and update ONLY the matching query for this task's module AND sprint
      const allQueries = queryClient.getQueriesData<any>({
        predicate: q => Array.isArray(q.queryKey) && q.queryKey[0] === 'tasks',
      })

      // Get the task's sprint context for filtering
      const taskSprintId = (task as any).sprintId as string | null | undefined
      const taskCycleInstanceId = (task as any).cycleInstanceId as
        | string
        | null
        | undefined
      // Determine the sprint key for this task (matching query key format from useTaskData)
      const taskSprintKey =
        taskSprintId || taskCycleInstanceId
          ? `sprint:${taskSprintId || taskCycleInstanceId}`
          : 'sprint:none'

      for (const [queryKey, oldData] of allQueries) {
        if (!oldData) continue

        // Check if this query is for the same module as the new task
        // Query key format: ['tasks', moduleType, moduleId, 'sprint:${sprintId}', 'noSprint:${noSprint}', filterKey]
        const queryModuleId = queryKey[2]

        // ONLY update if moduleId matches - skip all other queries entirely
        if (queryModuleId !== taskModuleId) continue

        // Also check sprint context to avoid adding task to wrong sprint caches
        // Position 3 contains 'sprint:${sprintId}' or 'sprint:none'
        const querySprintKey = queryKey[3] as string | undefined
        if (
          typeof querySprintKey === 'string' &&
          querySprintKey.startsWith('sprint:')
        ) {
          // If query has sprint context, it must match the task's sprint
          if (querySprintKey !== taskSprintKey) continue
        }

        const taskArray = oldData.tasks || oldData.data
        if (!Array.isArray(taskArray)) continue

        // Check if already exists (by real ID)
        if (taskArray.some((t: TaskWithAssignments) => t.id === task.id))
          continue

        // Check for temp task to replace - be lenient, just match by context (moduleId or cycleInstanceId)
        const taskCycleId = (task as any).cycleInstanceId
        const tempIdx = taskArray.findIndex((t: any) => {
          if (!t.id?.startsWith('temp-') && !t.isTemporary) return false
          // Match by cycleInstanceId if present, otherwise by moduleId
          if (taskCycleId && (t as any).cycleInstanceId === taskCycleId)
            return true
          if ((t as any).moduleId === taskModuleId) return true
          return false
        })

        if (tempIdx !== -1) {
          // Replace temp task with real task
          const newArray = [...taskArray]
          newArray[tempIdx] = task as TaskWithAssignments
          queryClient.setQueryData(
            queryKey,
            oldData.tasks
              ? { ...oldData, tasks: newArray }
              : { ...oldData, data: newArray },
          )
          continue
        }

        // Add new task to this specific query only (no temp task found to replace)
        queryClient.setQueryData(
          queryKey,
          oldData.tasks
            ? {
                ...oldData,
                tasks: [task, ...taskArray],
                totalCount: (oldData.totalCount || 0) + 1,
              }
            : {
                ...oldData,
                data: [task, ...taskArray],
                totalCount: (oldData.totalCount || 0) + 1,
              },
        )
      }

      // Also update 'my-tasks' queries if the task is assigned to any user in those queries
      // This handles personal tasks and tasks assigned to the current user
      const taskAssignees = (task as any).assignees as
        | Array<{ id: string } | string>
        | undefined
      const assigneeIds =
        taskAssignees?.map(a => (typeof a === 'string' ? a : a.id)) || []

      const myTasksQueries = queryClient.getQueriesData<any>({
        predicate: q =>
          Array.isArray(q.queryKey) && q.queryKey[0] === 'my-tasks',
      })

      for (const [queryKey, oldData] of myTasksQueries) {
        if (!oldData?.tasks) continue

        // Query key format: ['my-tasks', userId, includeCompleted]
        const queryUserId = queryKey[1] as string | undefined
        if (!queryUserId) continue

        // Check if task is assigned to or owned by this user
        const isAssignedToUser = assigneeIds.includes(queryUserId)
        const isOwnedByUser = taskModuleId === queryUserId

        if (!isAssignedToUser && !isOwnedByUser) continue

        // Check if task already exists
        if (oldData.tasks.some((t: TaskWithAssignments) => t.id === task.id))
          continue

        // Add the task to the cache
        queryClient.setQueryData(queryKey, {
          ...oldData,
          tasks: [task as TaskWithAssignments, ...oldData.tasks],
          totalCount: (oldData.totalCount || 0) + 1,
        })
      }
    },

    onTaskUpdated: (task: Task) => {
      const shouldSkip = shouldSkipUpdate(task.id, task)
      debugLogTasksRealtime(
        `onTaskUpdated:${task.id}`,
        'useTasksRealtime onTaskUpdated',
        {
          taskId: task.id,
          shouldSkip,
        },
      )

      if (shouldSkip) {
        return
      }

      // Echoes of our own instant-add session edits arrive under the realId,
      // which the list caches don't contain (the row is keyed by its tempId).
      // Processing them would hit the unknown-task membership invalidation
      // below and refetch every active list mid-edit — skip until the session
      // ends and the row settles under its real id.
      if (isInstantAddSessionTaskId(task.id)) {
        debugLogTasksRealtime(
          `onTaskUpdated:${task.id}`,
          'useTasksRealtime onTaskUpdated skipped: instant-add session active',
        )
        return
      }

      // Surgically update only the changed fields, and avoid reverting recently applied fields (like name)
      // by comparing against current cache snapshot
      const getCachedTaskSnapshot = (): TaskWithAssignments | undefined => {
        // 1) Single task cache
        const single = queryClient.getQueryData<TaskWithAssignments>([
          'task',
          task.id,
        ])
        if (single) return single
        // 2) Any tasks list caches
        const tasksQueries = queryClient.getQueriesData<any>({
          predicate: q =>
            Array.isArray(q.queryKey) && q.queryKey[0] === 'tasks',
        })
        for (const [, data] of tasksQueries) {
          if (!data) continue
          const arr = Array.isArray(data?.tasks)
            ? data.tasks
            : Array.isArray(data?.data)
              ? data.data
              : undefined
          if (Array.isArray(arr)) {
            const found = (arr as TaskWithAssignments[]).find(
              t => t.id === task.id,
            )
            if (found) return found
          }
        }
        // 3) Any subtasks caches
        const subtasksQueries = queryClient.getQueriesData<
          TaskWithAssignments[]
        >({
          predicate: q =>
            Array.isArray(q.queryKey) && q.queryKey[0] === 'subtasks',
        })
        for (const [, arr] of subtasksQueries) {
          if (Array.isArray(arr)) {
            const found = arr.find(t => t.id === task.id)
            if (found) return found
          }
        }
        return undefined
      }

      const current = getCachedTaskSnapshot()
      const incoming = normalizeTaskUpdates(
        task as unknown as TaskWithAssignments,
      ) as Partial<TaskWithAssignments>
      const incomingWithFallback = preserveAvailableStatuses(
        current,
        incoming as Partial<TaskRecord>,
      ) as Partial<TaskWithAssignments>

      let filteredUpdates: Partial<TaskWithAssignments> = incomingWithFallback
      const changedKeys: string[] = []
      if (current) {
        // Compute shallow changed keys
        const keysToCheck: Array<keyof TaskWithAssignments> = [
          'name',
          'description',
          'status',
          'statusTypeId',
          'completedAt',
          'priority',
          'scheduledFor',
          'dueDate',
          'order',
          'groupId',
          'sprintId',
          'cycleInstanceId',
          'assignees',
        ]
        keysToCheck.forEach(key => {
          const a = current[key]
          const b = incoming[key]
          const isDifferent =
            a instanceof Date || b instanceof Date
              ? a && b
                ? String(a) !== String(b)
                : a !== b
              : JSON.stringify(a) !== JSON.stringify(b)
          if (isDifferent) changedKeys.push(key)
        })

        // If name differs along with unrelated fields (e.g., dueDate/order), and we recently applied a name,
        // prefer keeping current name to avoid flicker/revert from stale snapshots.
        if (
          changedKeys.includes('name') &&
          (changedKeys.includes('dueDate') || changedKeys.includes('order'))
        ) {
          const recentMs = 5000 // 5s safety window
          const lastNameTs = getLastFieldApplied(task.id, 'name')
          if (Date.now() - lastNameTs < recentMs) {
            filteredUpdates = { ...incoming }
            delete (filteredUpdates as any).name
          }
        }
      }

      // For subtasks, only update the specific subtasks cache - no broad queries
      const parentId = (task as any)?.parentTaskId as string | undefined
      if (parentId) {
        const subtaskCacheKey = ['subtasks', parentId] as const
        const existingSubtasks =
          queryClient.getQueryData<TaskWithAssignments[]>(subtaskCacheKey)
        const cachedSubtask = existingSubtasks?.find(t => t.id === task.id)
        const completedDelta = cachedSubtask
          ? Number(isCompletedTaskForSummary(task as TaskWithAssignments)) -
            Number(isCompletedTaskForSummary(cachedSubtask))
          : 0

        queryClient.setQueryData(
          subtaskCacheKey,
          (old: TaskWithAssignments[] | undefined) => {
            if (!old) return old
            const idx = old.findIndex(t => t.id === task.id)
            if (idx === -1) return old
            const updated = [...old]
            const sanitized = preserveAvailableStatuses(
              old[idx],
              stripUndefinedValues(task as unknown as TaskRecord),
            )
            updated[idx] = {
              ...old[idx],
              ...(sanitized as Partial<TaskWithAssignments>),
            } as TaskWithAssignments
            return sortTasksByPosition(updated)
          },
        )
        if (completedDelta !== 0) {
          updateParentSubtaskSummaryQueries(queryClient, parentId, {
            completedDelta,
          })
        }
        // DO NOT touch main task queries - subtasks are excluded from them anyway
        return
      }

      // For regular tasks, only update via surgicallyUpdateTask (which is already targeted)
      surgicallyUpdateTask(task.id, filteredUpdates as Partial<Task>)
      invalidateMembershipSensitiveTaskQueries(
        queryClient,
        task as unknown as Partial<TaskWithAssignments>,
        current,
        changedKeys,
      )
    },

    onTaskDeleted: (data: {
      id?: string
      taskIds?: string[]
      organizationId: string
      _bulk?: boolean
      parentTaskId?: string | null
      statusTypeId?: string | null
      status?: string | null
      completedAt?: Date | string | null
    }) => {
      // Handle bulk delete - single event with multiple task IDs
      const deletedIds =
        data._bulk && data.taskIds ? data.taskIds : data.id ? [data.id] : []
      debugLogTasksRealtime('onTaskDeleted', 'useTasksRealtime onTaskDeleted', {
        deletedCount: deletedIds.length,
        organizationId: data.organizationId,
        bulk: !!data._bulk,
      })

      if (deletedIds.length === 0) return

      const deletedIdSet = new Set(deletedIds)
      const countedDeletedSubtasks = new Set<string>()
      const parentSummaryDeltas = new Map<
        string,
        { subtaskDelta: number; completedDelta: number }
      >()
      const addParentSummaryDelta = (
        parentTaskId: string | undefined | null,
        subtask: Parameters<typeof isCompletedTaskForSummary>[0],
      ) => {
        if (!parentTaskId) return
        const current = parentSummaryDeltas.get(parentTaskId) || {
          subtaskDelta: 0,
          completedDelta: 0,
        }
        current.subtaskDelta -= 1
        if (isCompletedTaskForSummary(subtask)) {
          current.completedDelta -= 1
        }
        parentSummaryDeltas.set(parentTaskId, current)
      }

      const subtasksQueries = queryClient.getQueriesData<TaskWithAssignments[]>(
        {
          predicate: q =>
            Array.isArray(q.queryKey) && q.queryKey[0] === 'subtasks',
        },
      )

      for (const [queryKey, oldData] of subtasksQueries) {
        if (!Array.isArray(oldData)) continue
        const parentTaskId =
          Array.isArray(queryKey) && typeof queryKey[1] === 'string'
            ? queryKey[1]
            : undefined

        for (const subtask of oldData) {
          if (!deletedIdSet.has(subtask.id)) continue
          countedDeletedSubtasks.add(subtask.id)
          addParentSummaryDelta(parentTaskId || subtask.parentTaskId, subtask)
        }
      }

      if (
        data.id &&
        deletedIdSet.has(data.id) &&
        data.parentTaskId &&
        !countedDeletedSubtasks.has(data.id)
      ) {
        addParentSummaryDelta(data.parentTaskId, data)
      }

      // Remove deleted task detail caches first. This includes legacy
      // ['task', id], task detail, and task-by-number queries whose cached
      // payload points at a deleted id.
      deletedIds.forEach(taskId => {
        queryClient.removeQueries({ queryKey: ['task', taskId] })
        queryClient.removeQueries({ queryKey: ['tasks', 'detail', taskId] })
      })
      queryClient.removeQueries({
        predicate: query => {
          if (!Array.isArray(query.queryKey)) return false
          if (query.queryKey[0] !== 'tasks') return false

          const data = queryClient.getQueryData<TaskWithAssignments>(
            query.queryKey,
          )

          return !!data?.id && deletedIdSet.has(data.id)
        },
      })

      const shouldInvalidateAfterBulk = data._bulk && deletedIds.length > 5

      // Surgically update caches first so deletions are reflected immediately.
      // Find and update ONLY queries that contain deleted tasks - skip all others entirely
      const allQueries = queryClient.getQueriesData<any>({
        predicate: q =>
          Array.isArray(q.queryKey) &&
          (q.queryKey[0] === 'tasks' ||
            q.queryKey[0] === 'subtasks' ||
            q.queryKey[0] === 'my-tasks'),
      })

      for (const [queryKey, oldData] of allQueries) {
        if (!oldData) continue

        // Check if this query contains any deleted tasks
        const taskArray = Array.isArray(oldData)
          ? oldData
          : oldData.tasks || oldData.data
        if (!Array.isArray(taskArray)) continue

        const hasDeletedTasks = taskArray.some((t: TaskWithAssignments) =>
          deletedIdSet.has(t.id),
        )
        if (!hasDeletedTasks) continue // No deleted tasks in this query - skip entirely

        const filteredTasks = taskArray.filter(
          (t: TaskWithAssignments) => !deletedIdSet.has(t.id),
        )
        const deletedCount = taskArray.length - filteredTasks.length

        // Only update this specific query
        if (Array.isArray(oldData)) {
          // Subtasks array
          queryClient.setQueryData(queryKey, filteredTasks)
        } else if (oldData.tasks) {
          queryClient.setQueryData(queryKey, {
            ...oldData,
            tasks: filteredTasks,
            totalCount: Math.max(0, (oldData.totalCount || 0) - deletedCount),
          })
        } else if (oldData.data) {
          queryClient.setQueryData(queryKey, {
            ...oldData,
            data: filteredTasks,
            totalCount: Math.max(0, (oldData.totalCount || 0) - deletedCount),
          })
        }
      }

      for (const [parentTaskId, deltas] of parentSummaryDeltas) {
        updateParentSubtaskSummaryQueries(queryClient, parentTaskId, deltas)
      }

      // Bulk deletes prune active caches above. Mark inactive caches stale for
      // consistency without forcing active list/detail queries to refetch
      // deleted tasks back into view while the UI is closing detail panes.
      if (shouldInvalidateAfterBulk) {
        queryClient.invalidateQueries({
          queryKey: ['tasks'],
          refetchType: 'inactive',
        })
        queryClient.invalidateQueries({
          queryKey: ['my-tasks'],
          refetchType: 'inactive',
        })
      }
    },

    onTaskStatusChanged: (data: any) => {
      debugLogTasksRealtime(
        `onTaskStatusChanged:${data?.taskId}`,
        'useTasksRealtime onTaskStatusChanged',
        {
          taskId: data?.taskId,
        },
      )

      // IMPORTANT: Don't process status changes that come from task updates
      // This prevents double processing when onTaskUpdated already handles status changes
      if (shouldSkipUpdate(data.taskId, { taskId: data.taskId })) {
        return
      }

      // Update only the status field
      if (data.task) {
        surgicallyUpdateTask(data.taskId, {
          status: data.task.status,
          statusTypeId: data.task.statusTypeId,
          updatedAt: data.task.updatedAt,
        })
      }
    },

    onTaskAssigned: (data: any) => {
      debugLogTasksRealtime(
        `onTaskAssigned:${data?.taskId}`,
        'useTasksRealtime onTaskAssigned',
        {
          taskId: data?.taskId,
        },
      )

      if (shouldSkipUpdate(data.taskId, { taskId: data.taskId })) {
        return
      }
      // If server provided full task snapshot, apply surgically for instant UI
      // Use surgical update to avoid global avatar flashing
      if (data.task) {
        surgicallyUpdateTask(data.taskId, {
          assignees: data.task.assignees,
          updatedAt: data.task.updatedAt,
        } as Partial<Task>)
      } else {
        // Fallback: notify subscribers without network refetch and without global events
        queryClient.invalidateQueries({
          queryKey: ['task', data.taskId],
          exact: true,
          refetchType: 'none',
        })
      }
    },

    onTaskUnassigned: (data: any) => {
      debugLogTasksRealtime(
        `onTaskUnassigned:${data?.taskId}`,
        'useTasksRealtime onTaskUnassigned',
        {
          taskId: data?.taskId,
        },
      )

      if (shouldSkipUpdate(data.taskId, { taskId: data.taskId })) {
        return
      }
      // Use surgical update to avoid global avatar flashing
      if (data.task) {
        surgicallyUpdateTask(data.taskId, {
          assignees: data.task.assignees,
          updatedAt: data.task.updatedAt,
        } as Partial<Task>)
      } else {
        // Fallback: notify subscribers without network refetch and without global events
        queryClient.invalidateQueries({
          queryKey: ['task', data.taskId],
          exact: true,
          refetchType: 'none',
        })
      }
    },

    enableBroadcastChannel: true,
  })
}
